import axios from 'axios';
import phone from 'phone';
import { FunctionFactory, ItemValue, Model, Question } from 'survey-core';
import { IDiscount } from '../types/discount';
import { setShowDetails } from './storage';
import { SELECTED_COUNTRY_STATE_KEY } from '../components/questionTypes/MuiCountries';
import { COMPANY_STATE_KEY, MANUAL_ENTERING_STATE_KEY } from '../components/questionTypes/MuiCompanySearch';
import { get, isUndefined, round } from 'lodash';
import { CompanyLocation, ProductTypes, SupportServices, SurveySteps } from '../types/enums';
import { PageModel } from 'survey-core/survey.core';
import { CurrentPageChangingEvent } from 'survey-core/typings/survey-events-api';
import { OperationType } from '../types/companyDetails';

const emailExistsCache = {};
const companyExistsCache = {};

type ExpressionFunction = {
  question: Question;
  survey: Model;
};

type CustomPageModel = PageModel & {
  stepNumber: number;
  stepName: SurveySteps;
};

type AsyncExpressionFunction = ExpressionFunction & {
  returnResult(result?: boolean): void;
};

enum SearchProvider {
  COMPANY_HOUSE,
  ABN,
  ACN
}

export const registerCustomFunctions = () => {
  FunctionFactory.Instance.register('checkIfEmailExists', checkIfEmailExists, true);
  FunctionFactory.Instance.register('expandChoicesByUrl', expandChoicesByUrl);
  FunctionFactory.Instance.register('validatePhoneNumber', validatePhoneNumber);
  FunctionFactory.Instance.register('getJoiningFee', getJoiningFee);
  FunctionFactory.Instance.register('formatPrice', formatPrice);
  FunctionFactory.Instance.register('getPrice', getPrice);
  FunctionFactory.Instance.register('getPriceWithDiscount', getPriceWithDiscount);
  FunctionFactory.Instance.register('showDetails', showDetails, true);
  FunctionFactory.Instance.register('resetCompanyDetails', resetCompanyDetails);
  FunctionFactory.Instance.register('checkIfCompanyNameExists', checkIfCompanyNameExists, true);
  FunctionFactory.Instance.register('showPackageType', showPackageType);
  FunctionFactory.Instance.register('showAdditionalProductExplanationText', showAdditionalProductExplanationText);
  FunctionFactory.Instance.register('getPriceTotal', getPriceTotal);
  FunctionFactory.Instance.register('showAdditionalProductName', showAdditionalProductName);
};

export async function checkIfEmailExists(this: AsyncExpressionFunction, [value]: string[]) {
  const { returnResult, question, survey } = this;

  if (!value || question.containsErrors) {
    returnResult();
    return;
  }
  if (emailExistsCache[value] !== undefined) {
    // Skip checking the same email twice
    survey.setVariable('userExists', emailExistsCache[value]);
    setTimeout(() => returnResult(true), 0);
    return;
  }

  // Returns false when the email is available
  const response = await axios.get<boolean>(`/api/v1/signup/validate-user?email=${encodeURIComponent(value)}`);
  emailExistsCache[value] = response.data;

  survey.setVariable('userExists', response.data);
  survey.setVariable('showUserExistsDialog', response.data);
  returnResult(true);
}

export async function checkIfCompanyNameExists(this: AsyncExpressionFunction, [value]: string[]) {
  const { returnResult, question, survey } = this;

  const company = survey.getVariable('company');
  const country = survey.getQuestionByName('country');
  const postalCode = survey.getQuestionByName('postalCode');
  const someHasError = [country, postalCode, question].some(q => q.containsErrors || !q.value);
  const sanitizedValue = (value || '').trim();

  if (!sanitizedValue || someHasError) {
    returnResult();
    return;
  }

  const params = `companyName=${encodeURIComponent(sanitizedValue)}&postalCode=${encodeURIComponent(
    postalCode.value.trim()
  )}&country=${encodeURIComponent(country.value)}`;
  const body = [];
  if (company?.id) {
    body.push({ typeId: SearchProvider.COMPANY_HOUSE, value: company.id });
  }
  if (company?.abn) {
    body.push({ typeId: SearchProvider.ABN, value: company.abn });
  }
  if (company?.acn) {
    body.push({ typeId: SearchProvider.ACN, value: company.acn });
  }
  const cacheKey = get(body, '[0].value', params);

  if (!isUndefined(companyExistsCache[cacheKey])) {
    // Skip checking the same company name twice
    setTimeout(() => returnResult(companyExistsCache[cacheKey]), 0);
    return;
  }

  // Returns false when the name is available
  // If there is no house number, send company data in query params
  const response = await axios.post<boolean>(`/api/v1/signup/validate-organization?${params}`, body);
  companyExistsCache[cacheKey] = !response.data;
  returnResult(!response.data);
}

function getProductsChoices(survey: Model) {
  const subscriptionQuestion = survey.getQuestionByName('subscription');
  return subscriptionQuestion.activeChoices.length > 0
    ? subscriptionQuestion.activeChoices // Prefer reading choices from the question
    : survey.getVariable('productsChoices') || [];
}

export function expandChoicesByUrl(this: ExpressionFunction, [findValue, property, findKey]: string[]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  const choice = choices.find((item: ItemValue) =>
    findKey ? item.originalItem[findKey] === findValue : item.value === findValue
  );

  return choice && choice.originalItem && choice.originalItem[property];
}

export function validatePhoneNumber(this: ExpressionFunction, [value]: string[]) {
  return phone(value, { validateMobilePrefix: false }).isValid;
}

export function getJoiningFee(this: ExpressionFunction, [key]: string[]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);
  const joiningFee = choices.find((item: ItemValue) => item.originalItem.subscriptionTier === 'CHAS Joining Fee');

  return !joiningFee ? null : joiningFee.originalItem[key || 'price'];
}

export function formatPrice(this: ExpressionFunction, [price]: number[]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  return choices[0]
    ? new Intl.NumberFormat(choices[0].originalItem.location, {
        style: 'currency',
        currency: choices[0].originalItem.currencyIso
      }).format(price)
    : price;
}

export function getPrice(this: ExpressionFunction, [productId, customPrice]: [string, number | undefined]) {
  const { survey } = this;
  const choices = getProductsChoices(survey);
  const choice = choices.find((item: ItemValue) => item.value === productId);

  if (choice && choice.originalItem) {
    const { price } = choice.originalItem;

    return customPrice ?? price;
  }
  return 0;
}

export function getPriceWithDiscount(
  this: ExpressionFunction,
  [productId, applyOnlyByProductIdMatch]: [string, boolean | undefined]
) {
  const { survey } = this;
  const choices = getProductsChoices(survey);

  const choice = choices.find((item: ItemValue) => item.value === productId);

  if (choice && choice.originalItem) {
    const { price } = choice.originalItem;
    const discount = survey.getVariable('discount') as IDiscount | undefined;

    if (
      discount &&
      (discount.appliesTo.includes(productId) || (!applyOnlyByProductIdMatch && discount.appliesTo.length === 0))
    ) {
      return (price * 100 - round((price * discount.amount) / 100, 2) * 100) / 100;
    }
    return price;
  }
  return 0;
}

export function getPriceTotal(this: ExpressionFunction, [priceWithDiscount]: number[]) {
  const { survey } = this;
  const taxRate = survey.getVariable('taxRate');
  const taxPercent = taxRate?.isActive ? taxRate.ratePercentage : 0;

  return taxRate
    ? (priceWithDiscount * 100 + round((priceWithDiscount * taxPercent) / 100, 2) * 100) / 100
    : priceWithDiscount;
}

export function showDetails(this: AsyncExpressionFunction) {
  const { survey, question, returnResult } = this;
  if (question.containsErrors) {
    returnResult();
    return;
  }
  survey.setVariable('showDetails', true);
  setShowDetails();
  returnResult(true);
}

export function showPackageType() {
  const country = new URLSearchParams(window.location.search).get('country');
  return country !== CompanyLocation.GB && country !== CompanyLocation.AU;
}

export function resetCompanyDetails(this: ExpressionFunction) {
  const { survey } = this;

  survey.setVariable('company', undefined);
  survey.setVariable('showDetails', false);
  survey.setVariable('manualEntering', false);
  survey.setVariable('savedCountry', undefined);
  sessionStorage.removeItem(COMPANY_STATE_KEY);
  setShowDetails(false);
  sessionStorage.removeItem(MANUAL_ENTERING_STATE_KEY);
  sessionStorage.removeItem(SELECTED_COUNTRY_STATE_KEY);

  const questions = survey.getQuestionsByNames([
    'companyName',
    'country',
    'address1',
    'address2',
    'townOrCity',
    'postalCode'
  ]);

  questions.forEach(question => {
    question.clearValue();
    question.clearErrors();
  });
}

export function showAdditionalProductName(this: ExpressionFunction) {
  const { survey } = this;
  const subscriptionQuestion = survey.getQuestionByName('additionalProduct');
  const choices = getProductsChoices(survey);
  const selection = choices.find((item: ItemValue) => item.value === subscriptionQuestion.value);
  return selection?.originalItem?.purchaseType;
}

export function showAdditionalProductExplanationText(this: AsyncExpressionFunction, [selectedProduct]: [string]) {
  const { survey } = this;
  const choices = getProductsChoices(survey).filter((item: ItemValue) => {
    if (selectedProduct) {
      return (
        item.originalItem.subscriptionTier === selectedProduct &&
        item.originalItem.productType === ProductTypes.Services
      );
    }

    return false;
  });

  return choices.some((item: ItemValue) => {
    return (
      item.originalItem.purchaseType === SupportServices.FastTrack ||
      item.originalItem.purchaseType === SupportServices.CHASAssist
    );
  });
}

export function getShouldUpdateLead(survey: Model, refChanged: boolean, options: CurrentPageChangingEvent) {
  const userExists = survey.getVariable('userExists');
  const signUpType = survey.getVariable('signupType');
  const companyDetailsStepNumber = signUpType === OperationType.SIGNUP ? 3 : 2;
  const page: CustomPageModel = survey.activePage;
  const orderPage = survey.pages.find((page: CustomPageModel) => page.stepName === SurveySteps.Order);

  if (userExists && page.stepNumber < companyDetailsStepNumber && !orderPage?.wasShown) {
    return false;
  }

  var forceUpdate = page.stepName === SurveySteps.CompanyDetails;
  return (forceUpdate || refChanged) && options.isGoingForward;
}
